import {
  put,
  takeLatest,
  call,
  take,
  race,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import _ from 'lodash';
import queryString from 'query-string';
import { change, initialize } from 'redux-form';
import { geocodeByAddress } from 'react-places-autocomplete';
import {
  saveUserCredentials,
  saveRegistrationParams,
  flushUser,
  fetchUser,
} from '../../services/auth';
import {
  REGISTER_USER,
  FETCH_ADDRESSES,
  SELECT_ADDRESS,
  COMPLETE_REGISTRATION,
  POLL_EMAIL_VERIFICATION,
  STOP_POLL_EMAIL_VERIFICATION,
  RESEND_EMAIL_VERIFICATION,
  FETCH_PROFILE_FOR_COMPLETE_REGISTRATION,
  INIT_COMPLETE_REGISTRATION,
} from '../constants';
import {
  EMAIL_DUPLICATE_ERROR_MESSAGE,
  LEXID_DUPLICATE_ERROR_MESSAGE,
  VSE_MATCH_ERROR_MESSAGE,
  PHONE_DUPLICATE_ERROR_MESSAGE,
} from '../../common/lib/constants';
import { setRegistrationProfile, getRegistrationProfile } from '../../common/lib/registrationProfileSession';
import { setVerificationLink } from '../../common/lib/verificationLinkSession';
import { mapProfileToRegisterFields } from '../../common/lib/mapProfileToRegisterFields';
import { mapRegistrationProfileToForm } from '../../common/lib/mapRegistrationProfileToForm';
import { parseRegisterEmailVerifyResponse } from '../../common/lib/parseRegisterEmailVerifyResponse';
import { buildRegisterVerifyProfileBody } from '../../common/lib/buildProfilePayloads';
import { getCopy } from '../../common/lib/copyDefaults';

import {
  registerUserSuccess,
  registerUserFailure,
  completeRegistrationSuccess,
  completeRegistrationFailure,
  pollEmailVerificationSuccess,
  pollEmailVerificationFailure,
  fetchProfileForCompleteRegistration,
  fetchProfileForCompleteRegistrationSuccess,
  fetchProfileForCompleteRegistrationFailure,
  fetchAddressesSuccess,
  fetchAddressesFailure,
  selectAddressSuccess,
  selectAddressFailure,
  resendEmailVerificationSuccess,
  resendEmailVerificationFailure,
  resendEmailCooldownTick,
  resendEmailCooldownExpire,
} from '../actions';
import { gtagEvent } from '../../common/lib/analyticsEventManager';

const POLL_INTERVAL_MS = 8000;
const MAX_POLL_ATTEMPTS = 150;
const RESEND_COOLDOWN_SECONDS = 30;

const validateParsedAddress = (address) => {
  return address &&
    ((address.streetNumber && address.street) || address.premise) &&
    address.city &&
    address.state &&
    address.zip;
};

const parseAddress = (address) => {
  const addressComponents = _.get(address, '0.address_components', false);
  const parsedAddress = {
    streetNumber: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    premise: null,
  };
  if (addressComponents) {
    addressComponents.forEach((component) => {
      if (component.types.includes('street_number')) {
        parsedAddress.streetNumber = component.short_name;
      } else if (component.types.includes('route')) {
        parsedAddress.street = component.short_name;
      } else if (component.types.includes('locality')) {
        parsedAddress.city = component.short_name;
      } else if (component.types.includes('sublocality')) {
        parsedAddress.city = component.short_name;
      } else if (component.types.includes('administrative_area_level_1')) {
        parsedAddress.state = component.short_name;
      } else if (component.types.includes('postal_code')) {
        parsedAddress.zip = component.short_name;
      } else if (component.types.includes('premise')) {
        parsedAddress.premise = component.short_name;
      }
    });
  }
  return parsedAddress;
};

const resolveBirthdate = (profileFields) => {
  const cache = getRegistrationProfile();
  if (cache && cache.birthdate) {
    return cache.birthdate;
  }
  if (profileFields && profileFields.birthdate) {
    return profileFields.birthdate;
  }
  const user = fetchUser();
  if (user.identity && user.identity.birthdate) {
    return user.identity.birthdate;
  }
  return null;
};

const createRegisterSagas = (api) => {
  function* registerUserSaga(action) {
    const {
      registerUserForm,
      push,
    } = action.payload;
    try {
      const queryParams = queryString.parse(window.location.search);
      const callbackUri = _.get(queryParams, 'callbackUri', false);
      const clientId = _.get(queryParams, 'clientId', false);
      const response = yield call(api.postInitialRegister, { registerUserForm });
      const statusCode = _.get(response, 'data.data.statusCode', false);
      const hasTokens = _.get(response, 'data.data.AccessToken', false);
      const phoneDuplicate = _.get(response, 'data.data.phoneDuplicate', false);
      const emailDuplicate = _.get(response, 'data.data.emailDuplicate', false);
      const vseMatch = _.get(response, 'data.data.vseMatch', false);
      const lexIdDuplicate = _.get(response, 'data.data.lexIdDuplicate', false);
      const duplicatedEmail = _.get(response, 'data.data.duplicatedEmail', '');

      if (statusCode === 200 && hasTokens) {
        const accessToken = _.get(response, 'data.data.AccessToken', null);
        const refreshToken = _.get(response, 'data.data.RefreshToken', null);
        const identityToken = _.get(response, 'data.data.IdToken', null);
        const expiresIn = _.get(response, 'data.data.ExpiresIn', null);

        saveUserCredentials({
          accessToken,
          refreshToken,
          identityToken,
          expiresIn,
        });
        saveRegistrationParams({
          clientId,
          callbackUri, 
        });
        setRegistrationProfile({
          firstName: registerUserForm.firstName,
          lastName: registerUserForm.lastName,
          phone: registerUserForm.phone,
          birthdate: registerUserForm.birthdate,
        });

        yield put(registerUserSuccess({
          accessToken,
          refreshToken,
          identityToken,
          expiresIn,
        }));
        gtagEvent('DC-5139115/nylot0/nylre0+standard');
        push('/check-email');
      } else if (phoneDuplicate) {
        yield put(registerUserFailure(statusCode, PHONE_DUPLICATE_ERROR_MESSAGE, duplicatedEmail));
      } else if (emailDuplicate) {
        yield put(registerUserFailure(statusCode, EMAIL_DUPLICATE_ERROR_MESSAGE, duplicatedEmail));
      } else if (lexIdDuplicate) {
        push(`/user-duplicated?clientId=${clientId}`);
        yield put(registerUserFailure(statusCode, LEXID_DUPLICATE_ERROR_MESSAGE, duplicatedEmail));
      } else if (vseMatch) {
        yield put(registerUserFailure(statusCode, VSE_MATCH_ERROR_MESSAGE, duplicatedEmail));
      } else {
        const errorDetail = _.get(response, 'data.errors[0].message', false)
          || _.get(response, 'data.errors[0].detail', false);
        const status = _.get(response, 'status', false);
        yield put(registerUserFailure(status, errorDetail));
      }
    } catch (e) {
      const errorDetail = _.get(e, 'response.data.errors.0.message', false);
      yield put(registerUserFailure({ errorDetail }));
    }
  }

  function* completeRegistrationSaga(action) {
    const {
      completeRegistrationForm,
      push,
    } = action.payload;
    try {
      const birthdate = resolveBirthdate(getRegistrationProfile());
      const body = buildRegisterVerifyProfileBody(completeRegistrationForm, birthdate);
      const response = yield call(api.putCompleteRegistration, body);
      const statusCode = _.get(response, 'data.data.statusCode', false);
      const httpStatus = _.get(response, 'status', false);
      const verificationLink = _.get(response, 'data.data.verificationLink', false);
      const emailVerified = _.get(response, 'data.data.emailVerified', true);
      const phoneDuplicate = _.get(response, 'data.data.phoneDuplicate', false);
      const lexIdDuplicate = _.get(response, 'data.data.lexIdDuplicate', false);
      const vseMatch = _.get(response, 'data.data.vseMatch', false);
      const duplicatedEmail = _.get(response, 'data.data.duplicatedEmail', '');

      if (statusCode === 200 || httpStatus === 200) {
        if (emailVerified === false) {
          push('/check-email');
          yield put(completeRegistrationFailure(statusCode, false));
          return;
        }
        if (phoneDuplicate) {
          yield put(completeRegistrationFailure(statusCode, PHONE_DUPLICATE_ERROR_MESSAGE));
        } else if (lexIdDuplicate) {
          const queryParams = queryString.parse(window.location.search);
          const clientId = _.get(queryParams, 'clientId', false);
          push(`/user-duplicated?clientId=${clientId}`);
          yield put(completeRegistrationFailure(statusCode, LEXID_DUPLICATE_ERROR_MESSAGE, duplicatedEmail));
        } else if (vseMatch) {
          yield put(completeRegistrationFailure(statusCode, VSE_MATCH_ERROR_MESSAGE));
        } else if (verificationLink) {
          setVerificationLink(verificationLink);
          yield put(completeRegistrationSuccess({ verificationLink }));
          push('/idv/identity-verification');
        } else {
          const errorDetail = _.get(response, 'data.errors[0].message', 'Registration could not be completed.');
          yield put(completeRegistrationFailure(statusCode, errorDetail));
        }
      } else {
        const errorDetail = _.get(response, 'data.errors[0].message', false);
        yield put(completeRegistrationFailure(httpStatus, errorDetail));
      }
    } catch (e) {
      const errorDetail = _.get(e, 'response.data.errors.0.message', false);
      yield put(completeRegistrationFailure({ errorDetail }));
    }
  }

  function* pollEmailVerificationLoop(action) {
    const { push } = action.payload;
    let attempts = 0;
    try {
      while (attempts < MAX_POLL_ATTEMPTS) {
        const response = yield call(api.getRegisterEmailVerify);
        const { unauthorized, emailVerified } = parseRegisterEmailVerifyResponse(response);

        if (unauthorized) {
          flushUser();
          yield put(pollEmailVerificationFailure({ statusCode: 401 }));
          push('/register');
          return;
        }

        yield put(pollEmailVerificationSuccess({ emailVerified }));

        if (emailVerified) {
          push('/complete-registration');
          return;
        }

        attempts += 1;
        yield delay(POLL_INTERVAL_MS);
      }
    } catch (e) {
      const errorDetail = _.get(e, 'response.data.errors.0.message', false);
      yield put(pollEmailVerificationFailure({ errorDetail }));
    }
  }

  function* watchPollEmailVerification() {
    while (true) {
      const action = yield take(POLL_EMAIL_VERIFICATION);
      yield race({
        poll: call(pollEmailVerificationLoop, action),
        stop: take(STOP_POLL_EMAIL_VERIFICATION),
      });
    }
  }

  function* runResendCooldown() {
    for (let remaining = RESEND_COOLDOWN_SECONDS; remaining > 0; remaining -= 1) {
      yield put(resendEmailCooldownTick({ secondsRemaining: remaining }));
      yield delay(1000);
    }
    yield put(resendEmailCooldownExpire());
  }

  function* resendEmailVerificationSaga(action) {
    const { push } = action.payload;
    const copy = getCopy('/check-email');
    try {
      const response = yield call(api.getRegisterEmailVerify, { resend: true });
      const parsed = parseRegisterEmailVerifyResponse(response);

      if (parsed.unauthorized) {
        flushUser();
        yield put(resendEmailVerificationFailure({ message: '' }));
        push('/register');
        return;
      }

      if (parsed.emailVerified) {
        push('/complete-registration');
        return;
      }

      if (parsed.httpError && parsed.httpError >= 500) {
        yield put(resendEmailVerificationFailure({
          message: copy.resendSystemError,
          statusCode: parsed.httpError,
        }));
        yield call(runResendCooldown);
        return;
      }

      if (parsed.httpError) {
        const errorDetail = _.get(response, 'data.errors[0].message', copy.resendFailure);
        yield put(resendEmailVerificationFailure({
          message: errorDetail || copy.resendFailure,
          statusCode: parsed.httpError,
        }));
        yield call(runResendCooldown);
        return;
      }

      if (parsed.emailVerificationSent === true) {
        yield put(resendEmailVerificationSuccess({ message: copy.resendSuccess }));
        yield call(runResendCooldown);
        return;
      }

      yield put(resendEmailVerificationFailure({ message: copy.resendFailure }));
      yield call(runResendCooldown);
    } catch (e) {
      const errorDetail = _.get(e, 'response.data.errors.0.message', false);
      yield put(resendEmailVerificationFailure({
        message: errorDetail || copy.resendFailure,
      }));
      yield call(runResendCooldown);
    }
  }

  function* watchResendEmailVerification() {
    yield takeLatest(RESEND_EMAIL_VERIFICATION, resendEmailVerificationSaga);
  }

  function* initCompleteRegistrationSaga(action) {
    const { push } = action.payload;
    const user = fetchUser();
    if (!user.accessToken) {
      push('/register');
      return;
    }
    try {
      const response = yield call(api.getRegisterEmailVerify);
      const { unauthorized, emailVerified } = parseRegisterEmailVerifyResponse(response);
      if (unauthorized) {
        flushUser();
        push('/register');
        return;
      }
      if (!emailVerified) {
        push('/check-email');
        return;
      }
      const profileCache = getRegistrationProfile();
      if (profileCache) {
        yield put(initialize('completeRegistration', mapRegistrationProfileToForm(profileCache)));
        yield put(fetchProfileForCompleteRegistrationSuccess({ profileFields: profileCache }));
      } else {
        yield put(fetchProfileForCompleteRegistration());
      }
    } catch (e) {
      push('/register');
    }
  }

  function* fetchProfileForCompleteRegistrationSaga() {
    try {
      const response = yield call(api.fetchProfile);
      const httpStatus = _.get(response, 'status', false);
      if (httpStatus === 200) {
        const profile = _.get(response, 'data.data.attributes', false);
        const profileFields = mapProfileToRegisterFields(profile);
        if (profileFields && profileFields.birthdate) {
          setRegistrationProfile({
            firstName: profileFields.firstName,
            lastName: profileFields.lastName,
            phone: profileFields.phone,
            birthdate: profileFields.birthdate,
          });
        }
        yield put(fetchProfileForCompleteRegistrationSuccess({ profileFields }));
        yield put(initialize('completeRegistration', mapRegistrationProfileToForm(profileFields)));
      } else {
        yield put(fetchProfileForCompleteRegistrationFailure({ errorDetail: true }));
      }
    } catch (e) {
      yield put(fetchProfileForCompleteRegistrationFailure({ errorDetail: e }));
    }
  }

  function* fetchAddressesSaga(action) {
    const { input } = action.payload;
    try {
      const response = yield call(api.fetchPlaces, { input });
      const statusCode = _.get(response, 'status', false);
      if (statusCode === 200) {
        yield put(fetchAddressesSuccess({ response }));
      } else {
        const errorDetail = _.get(response, 'data.errors.0.message', false);
        yield put(fetchAddressesFailure({
          statusCode,
          errorDetail,
        }));
      }
    } catch (e) {
      const errorDetail = _.get(e.response, 'data.errors.0.message', false);
      yield put(fetchAddressesFailure({ errorDetail }));
    }
  }

  function* selectAddressSaga(action) {
    const { address } = action.payload;
    try {
      const response = yield call(geocodeByAddress, address);
      const parsedAddress = parseAddress(response);
      if (validateParsedAddress(parsedAddress)) {
        yield put(selectAddressSuccess({
          address,
          parsedAddress,
        }));
        yield put(change('completeRegistration', 'address', address));
      } else {
        yield put(selectAddressFailure({ error: 'Your address must have a street number, street name, city, state and zipcode. Please try again.' }));
      }
    } catch (error) {
      yield put(selectAddressFailure({ error }));
    }
  }

  function* watchRegisterUser() {
    yield takeLatest(REGISTER_USER, registerUserSaga);
  }

  function* watchCompleteRegistration() {
    yield takeLatest(COMPLETE_REGISTRATION, completeRegistrationSaga);
  }

  function* watchInitCompleteRegistration() {
    yield takeLatest(INIT_COMPLETE_REGISTRATION, initCompleteRegistrationSaga);
  }

  function* watchFetchProfileForCompleteRegistration() {
    yield takeLatest(FETCH_PROFILE_FOR_COMPLETE_REGISTRATION, fetchProfileForCompleteRegistrationSaga);
  }

  function* watchFetchAddresses() {
    yield takeLatest(FETCH_ADDRESSES, fetchAddressesSaga);
  }

  function* watchSelectAddress() {
    yield takeLatest(SELECT_ADDRESS, selectAddressSaga);
  }

  return {
    watchRegisterUser,
    watchCompleteRegistration,
    watchPollEmailVerification,
    watchResendEmailVerification,
    watchInitCompleteRegistration,
    watchFetchProfileForCompleteRegistration,
    watchFetchAddresses,
    watchSelectAddress,
  };
};

export default createRegisterSagas;
